import React, { useState } from 'react';
import { Dumbbell, Users, QrCode, Bell, BarChart2, Shield, CheckCircle, ChevronRight, Phone, Clock, UserCheck, ChevronDown } from 'lucide-react';
import { supabase } from '../lib/supabase';

const FEATURES = [
  {
    icon: Users,
    title: '회원 관리',
    desc: '회원 정보를 한눈에 파악하고 빠르게 처리',
    points: ['회원 등록·수정·검색', '회원권 발급 및 갱신', '락커 배정 관리', '회원별 출석 이력 조회'],
  },
  {
    icon: QrCode,
    title: 'QR 출석 키오스크',
    desc: '태블릿 하나로 완성되는 무인 출석 시스템',
    points: ['회원 QR 스캔으로 1초 체크인', '전화번호 입력 방식 병행 지원', '음성 안내 자동 출력', '오늘 출석 현황 실시간 집계'],
  },
  {
    icon: Dumbbell,
    title: 'PT 관리',
    desc: 'PT 배정부터 일정, 기록까지 체계적으로',
    points: ['트레이너별 PT 배정 현황', 'PT 일정 예약 및 완료 처리', '잔여 횟수 자동 차감', '트레이너 전용 포털 제공'],
  },
  {
    icon: Bell,
    title: '스마트 알림',
    desc: '중요한 시점에 자동으로 알림을 발송',
    points: ['회원권 만료 7일 전 자동 알림', 'PT 잔여 3회 이하 경고', 'PT 수업 전날 일정 알림', '대표자 플랜 만료 안내'],
  },
  {
    icon: BarChart2,
    title: '매출 분석',
    desc: '숫자로 보는 헬스장 운영 현황',
    points: ['일·월별 매출 추이 그래프', '회원권 종류별 비중 분석', '신규·재등록 회원 통계', '수익 예측 대시보드'],
  },
  {
    icon: Shield,
    title: '회원 전용 앱',
    desc: '회원이 직접 확인하는 내 헬스장 정보',
    points: ['회원권 남은 기간 확인', 'PT 예정 일정 조회', '신체 정보 기록 및 변화 추이', 'QR 출석 코드 바로 표시'],
  },
];

const PRICING = [
  { period: '무료 체험', price: '0원', sub: '14일간 모든 기능 무제한', highlight: false },
  { period: '월 정기권', price: '49,000원', sub: '월 단위 갱신', highlight: true },
  { period: '연간 결제', price: '490,000원', sub: '2개월 무료', highlight: false },
];

const STEPS = [
  {
    step: '01',
    title: '체험 신청',
    desc: '아래 양식에 헬스장 정보를 입력하면 담당자가 1~2 영업일 내로 연락드립니다. 별도 설치나 계약 없이 바로 시작할 수 있습니다.',
  },
  {
    step: '02',
    title: '14일 무료 사용',
    desc: '회원 등록, QR 출석, PT 관리, 알림 등 모든 기능을 제한 없이 사용해보세요. 실제 운영 환경 그대로 체험하실 수 있습니다.',
  },
  {
    step: '03',
    title: '간편하게 구독',
    desc: '체험 후 계속 사용하고 싶다면 계좌이체 납부 후 앱 내에서 확인 요청하면 끝입니다. 복잡한 절차 없이 바로 연장됩니다.',
  },
];

const STATS = [
  { value: '무제한', label: '회원 등록' },
  { value: '14일', label: '무료 체험' },
  { value: '4가지', label: '역할별 포털' },
  { value: '24/7', label: '키오스크 운영' },
];

const FAQS = [
  {
    q: '설치가 필요한가요?',
    a: '아니요. 웹 브라우저에서 접속하는 웹앱이라 별도 설치가 필요 없습니다. PC, 태블릿, 스마트폰 모두 사용 가능합니다.',
  },
  {
    q: '트레이너 계정은 몇 개까지 만들 수 있나요?',
    a: '트레이너 계정 수에 제한이 없습니다. 초대 링크를 발급해 트레이너에게 공유하면 트레이너가 직접 계정을 만들고 바로 사용할 수 있습니다.',
  },
  {
    q: '회원이 직접 앱을 쓸 수 있나요?',
    a: '네. 회원은 전화번호로 로그인해 회원권 남은 기간, PT 일정, 신체 정보, QR 코드를 직접 확인할 수 있습니다.',
  },
  {
    q: 'QR 키오스크는 어떤 기기에서 쓰나요?',
    a: '태블릿(가로 모드 최적화)이나 PC 등 카메라가 달린 기기에서 사용할 수 있습니다. 카메라가 없는 경우 전화번호 입력 방식도 지원합니다.',
  },
  {
    q: '체험 종료 후 데이터는 사라지나요?',
    a: '체험 중 입력한 회원·PT 데이터는 그대로 유지됩니다. 구독 전환 후 별도 이전 작업 없이 계속 사용할 수 있습니다.',
  },
  {
    q: '결제는 어떻게 하나요?',
    a: '계좌이체 방식입니다. 국민은행 703001-01-598295 (티앤티 주식회사)로 입금 후, 앱 설정 화면에서 납부 확인 요청을 하시면 담당자가 확인 후 플랜을 연장해드립니다.',
  },
];

const TRIAL_STATUS = {
  idle: null,
  loading: '신청 중...',
  success: '신청이 완료되었습니다! 담당자가 연락드릴 예정입니다.',
  error: '오류가 발생했습니다. 다시 시도해주세요.',
};

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-100 dark:border-gray-700 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 text-left gap-4"
      >
        <span className="text-sm font-medium text-gray-900 dark:text-white">{q}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <p className="pb-4 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{a}</p>
      )}
    </div>
  );
}

export default function Landing({ onLogin }) {
  const [form, setForm] = useState({ name: '', gym_name: '', phone: '', email: '' });
  const [trialStatus, setTrialStatus] = useState('idle');
  const [showTrialForm, setShowTrialForm] = useState(false);

  async function handleTrialSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.gym_name || !form.phone || !form.email) return;
    setTrialStatus('loading');
    const { error } = await supabase.from('trial_requests').insert({
      name: form.name,
      gym_name: form.gym_name,
      phone: form.phone,
      email: form.email || null,
    });
    setTrialStatus(error ? 'error' : 'success');
    if (!error) setForm({ name: '', gym_name: '', phone: '', email: '' });
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white">

      {/* 헤더 */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm border-b border-gray-100 dark:border-gray-800">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
              <Dumbbell className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-lg">GYM Manager</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowTrialForm(true)}
              className="hidden sm:block text-sm text-primary-600 dark:text-primary-400 font-medium hover:underline"
            >
              무료 체험 신청
            </button>
            <button
              onClick={onLogin}
              className="btn-primary text-sm"
            >
              로그인
            </button>
          </div>
        </div>
      </header>

      {/* 히어로 */}
      <section className="pt-32 pb-20 px-4 text-center bg-gradient-to-b from-primary-50 to-white dark:from-gray-800 dark:to-gray-900">
        <div className="max-w-2xl mx-auto">
          <span className="inline-block text-xs font-semibold text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 px-3 py-1 rounded-full mb-4">
            헬스장 전용 관리 솔루션
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight mb-5">
            헬스장 운영,<br />
            <span className="text-primary-600 dark:text-primary-400">더 스마트하게</span>
          </h1>
          <p className="text-lg text-gray-500 dark:text-gray-400 mb-8 leading-relaxed">
            회원 관리부터 PT, QR 출석, 매출 분석까지<br />
            하나의 앱으로 완결되는 헬스장 관리 시스템
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => setShowTrialForm(true)}
              className="btn-primary text-base px-8 py-3 flex items-center gap-2 justify-center"
            >
              무료 14일 체험 신청 <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={onLogin}
              className="btn-secondary text-base px-8 py-3"
            >
              로그인
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-4">신용카드 불필요 · 14일 무료 · 언제든 취소 가능</p>
        </div>
      </section>

      {/* 숫자로 보는 GYM Manager */}
      <section className="py-10 px-4 bg-primary-600 dark:bg-primary-700">
        <div className="max-w-3xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-6 text-center text-white">
          {STATS.map(({ value, label }) => (
            <div key={label}>
              <p className="text-3xl font-extrabold mb-1">{value}</p>
              <p className="text-xs text-primary-100">{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 이렇게 사용해요 */}
      <section className="py-20 px-4 bg-white dark:bg-gray-900">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-2">이렇게 시작해요</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-12">복잡한 절차 없이 3단계로 바로 운영 시작</p>
          <div className="relative">
            {/* 연결선 (데스크탑) */}
            <div className="hidden sm:block absolute top-8 left-[calc(16.67%+1rem)] right-[calc(16.67%+1rem)] h-0.5 bg-primary-100 dark:bg-primary-900/50" />
            <div className="grid sm:grid-cols-3 gap-8">
              {STEPS.map(({ step, title, desc }) => (
                <div key={step} className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-primary-600 text-white flex items-center justify-center text-xl font-extrabold mb-4 relative z-10">
                    {step}
                  </div>
                  <h3 className="font-bold text-base mb-2">{title}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 기능 */}
      <section className="py-20 px-4 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-2">필요한 기능, 모두 있습니다</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-10">복잡한 설치 없이 웹 브라우저에서 바로 시작</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {FEATURES.map(({ icon: Icon, title, desc, points }) => (
              <div key={title} className="card p-5">
                <div className="w-10 h-10 bg-primary-50 dark:bg-primary-900/30 rounded-xl flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                </div>
                <h3 className="font-semibold text-sm mb-1">{title}</h3>
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">{desc}</p>
                <ul className="space-y-1">
                  {points.map(p => (
                    <li key={p} className="flex items-start gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 역할별 포털 */}
      <section className="py-20 px-4 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-2">모든 구성원을 위한 포털</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-10">대표자부터 트레이너, 회원까지 각자의 화면이 따로 있습니다</p>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="card p-5 border-l-4 border-primary-500">
              <div className="flex items-center gap-2 mb-3">
                <UserCheck className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                <span className="font-bold text-sm">대표자</span>
              </div>
              <ul className="space-y-1.5 text-xs text-gray-600 dark:text-gray-400">
                <li>· 회원·트레이너·락커 전체 관리</li>
                <li>· 매출 통계 및 분석</li>
                <li>· 납부 요청 및 알림 설정</li>
                <li>· QR 키오스크 운영</li>
              </ul>
            </div>
            <div className="card p-5 border-l-4 border-blue-500">
              <div className="flex items-center gap-2 mb-3">
                <Dumbbell className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <span className="font-bold text-sm">트레이너</span>
              </div>
              <ul className="space-y-1.5 text-xs text-gray-600 dark:text-gray-400">
                <li>· 담당 회원 PT 현황 조회</li>
                <li>· PT 일정 등록 및 완료 처리</li>
                <li>· 회원 신체 정보 기록</li>
                <li>· 예정 세션 캘린더 확인</li>
              </ul>
            </div>
            <div className="card p-5 border-l-4 border-green-500">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-5 h-5 text-green-600 dark:text-green-400" />
                <span className="font-bold text-sm">회원</span>
              </div>
              <ul className="space-y-1.5 text-xs text-gray-600 dark:text-gray-400">
                <li>· 회원권 남은 기간 확인</li>
                <li>· PT 예정 일정 조회</li>
                <li>· 신체 정보 변화 추이</li>
                <li>· QR 출석 코드 바로 표시</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 요금제 */}
      <section className="py-20 px-4 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-2">합리적인 요금제</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-10">회원 수 제한 없음 · 모든 기능 포함</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {PRICING.map(({ period, price, sub, highlight }) => (
              <div
                key={period}
                className={`card p-6 text-center ${highlight ? 'ring-2 ring-primary-500 dark:ring-primary-400' : ''}`}
              >
                {highlight && (
                  <span className="inline-block text-xs font-bold text-white bg-primary-600 px-2 py-0.5 rounded-full mb-3">
                    인기
                  </span>
                )}
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">{period}</p>
                <p className="text-2xl font-extrabold text-gray-900 dark:text-white mb-1">{price}</p>
                <p className="text-xs text-gray-400">{sub}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 p-4 bg-white dark:bg-gray-700 rounded-xl flex flex-wrap gap-3 justify-center">
            {['회원 수 무제한', '트레이너 계정 포함', 'QR 키오스크 포함', '모바일 최적화', '데이터 백업'].map(f => (
              <span key={f} className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                <CheckCircle className="w-3.5 h-3.5 text-green-500" />{f}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 bg-white dark:bg-gray-900">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-2">자주 묻는 질문</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-10">궁금한 점이 있으면 고객센터로 연락 주세요</p>
          <div className="card divide-y divide-gray-100 dark:divide-gray-700 px-5">
            {FAQS.map(faq => (
              <FaqItem key={faq.q} {...faq} />
            ))}
          </div>
        </div>
      </section>

      {/* 체험 신청 CTA */}
      <section className="py-20 px-4 bg-primary-600 dark:bg-primary-800">
        <div className="max-w-xl mx-auto text-center text-white">
          <h2 className="text-2xl font-bold mb-3">지금 바로 시작해보세요</h2>
          <p className="text-primary-100 mb-6 text-sm">14일 무료 체험, 설치 불필요, 언제든 취소 가능</p>
          <button
            onClick={() => setShowTrialForm(true)}
            className="bg-white text-primary-600 font-bold px-8 py-3 rounded-xl hover:bg-primary-50 transition-colors"
          >
            무료 체험 신청하기
          </button>
        </div>
      </section>

      {/* 푸터 */}
      <footer className="py-8 px-4 bg-gray-900 text-gray-400">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs mb-4">
            <div className="flex items-center gap-2">
              <Dumbbell className="w-4 h-4 text-primary-400" />
              <span className="font-semibold text-white">GYM Manager</span>
              <span>by 티앤티 주식회사</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> 1877-1827</span>
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> 평일 09:00~18:00</span>
            </div>
            <div className="flex gap-3">
              <a href="/terms" className="hover:text-white">이용약관</a>
              <a href="/privacy" className="hover:text-white font-semibold">개인정보처리방침</a>
            </div>
          </div>
          <p className="text-center text-xs text-gray-600">
            충북 충주시 호암토성2로3, 707호 · 사업자등록번호 105-88-17712 · ezbiz.manager@gmail.com
          </p>
        </div>
      </footer>

      {/* 체험 신청 모달 */}
      {showTrialForm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => { setShowTrialForm(false); setTrialStatus('idle'); }}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-sm shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">무료 체험 신청</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-5">담당자가 1~2 영업일 내 연락드립니다.</p>

            {trialStatus === 'success' ? (
              <div className="text-center py-6">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-sm text-gray-700 dark:text-gray-300">{TRIAL_STATUS.success}</p>
                <button onClick={() => { setShowTrialForm(false); setTrialStatus('idle'); }} className="mt-4 btn-primary text-sm">
                  닫기
                </button>
              </div>
            ) : (
              <form onSubmit={handleTrialSubmit} className="space-y-3">
                <div>
                  <label className="label">담당자명 <span className="text-red-500">*</span></label>
                  <input className="input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="홍길동" required />
                </div>
                <div>
                  <label className="label">헬스장 이름 <span className="text-red-500">*</span></label>
                  <input className="input" value={form.gym_name} onChange={e => setForm(p => ({ ...p, gym_name: e.target.value }))} placeholder="OO헬스장" required />
                </div>
                <div>
                  <label className="label">연락처 <span className="text-red-500">*</span></label>
                  <input className="input" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="010-0000-0000" required />
                </div>
                <div>
                  <label className="label">이메일 <span className="text-red-500">*</span></label>
                  <input type="email" className="input" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="example@email.com" required />
                </div>
                {trialStatus === 'error' && <p className="text-sm text-red-500">{TRIAL_STATUS.error}</p>}
                <div className="flex gap-2 pt-1">
                  <button type="submit" className="btn-primary flex-1 flex items-center justify-center gap-2" disabled={trialStatus === 'loading'}>
                    {trialStatus === 'loading' ? '신청 중...' : '신청하기'}
                  </button>
                  <button type="button" onClick={() => setShowTrialForm(false)} className="btn-secondary">취소</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
